import { FormConfig, Payload } from './AddEditRecord.type'
import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck, faAddressBook, faUsersRectangle, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons'
import { spudAPI } from '../../services/spud.service'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

export const SERVICE_FORM_ELEMENTS: Array<FormConfig> = [
  {
    title: 'Essential',
    ref: React.createRef(),
    icon: <FontAwesomeIcon icon={faListCheck as IconProp} />,
    fields: [
      {
        name: 'date_last_updated',
        label: 'Last updated date',
        type: 'hidden',
        defaultValue: '',
        rules: { required: false },
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'name',
        label: 'Service Name',
        type: 'text',
        rules: { required: true },
        defaultValue: '',
        highlight: true,
        hideAsterisk: true,
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'datasets',
        label: 'Dataset',
        type: 'multiselect',
        bulkAddEnabled: true,
        rules: {
          required: {
            value: true,
            message: 'Please select at least one database to which this service should export.',
          },
        },
        highlight: true,
        defaultValue: [],
        optionFunction: spudAPI.fetchDatasets,
        asyncParams: { recordType: 'service' },
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'description',
        label: 'Service Description',
        type: 'textarea',
        rules: { required: true },
        defaultValue: '',
        canShowLineBreaks: true,
        highlight: true,
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
        minHeight: '150px',
      },
      {
        name: 'crisis_keywords',
        label: 'Crisis Keywords',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        authorisedUserTypes: ['Administrator'],
        customComponent: {
          name: 'SPUDCrisisKeywordsAutocomplete',
          props: {
            title: 'Crisis Keywords',
          },
        },
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'funding_body',
        label: 'Funding Body',
        type: 'text',
        rules: {},
        defaultValue: '',
        hideAsterisk: true,
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'eligibility_info',
        label: 'Eligibility',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'ineligibility_info',
        label: 'Ineligibility',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'service_types',
        label: 'Service Types',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDServiceTypeClassification',
          props: {
            title: 'Service Types',
          },
        },
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'age_groups',
        label: 'Age Groups',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDAgeGroups',
          props: {
            name: 'Age Groups',
          },
        },
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'target_gender',
        label: 'Gender',
        type: 'multiselect',
        rules: {},
        defaultValue: ['unspecified'],
        options: [
          { name: 'Female', id: 'female' },
          { name: 'Male', id: 'male' },
          { name: 'Transgender', id: 'other' },
          { name: 'No specific gender selected', id: 'unspecified' },
        ],
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'indigenous_classification',
        label: 'Indigenous Classification',
        type: 'dropdown',
        rules: {},
        defaultValue: 'mainstream',
        options: [
          { name: 'Aboriginal (indigenous) specific', id: 'specific' },
          { name: 'Culturally safe for Aboriginal (indigenous)', id: 'culturally_safe' },
          { name: 'Mainstream who cater for Aboriginal (indigenous)', id: 'mainstream_who_cater' },
          { name: 'No Indigenous classification selected', id: 'mainstream' },
        ],
        serviceProviderEditable: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'is_cald_specific',
        label: 'CALD Specific',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'is_lgbtiqa_plus_specific',
        label: 'LGBTIQA+ Specific',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'is_ndis_approved',
        label: 'NDIS Approved',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'languages',
        label: 'Languages',
        type: 'multiselect',
        rules: {},
        defaultValue: [],
        optionFunction: spudAPI.fetchLanguages,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
    ],
  },
  {
    title: 'Contact',
    ref: React.createRef(),
    icon: <FontAwesomeIcon icon={faAddressBook as IconProp}/>,
    fields: [
      {
        name: 'phones',
        label: 'Contact',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDContact',
          props: {
            title: 'Contact Information',
            socialFormKey: 'social_media',
            contactFormKey: 'phones',
            recordType: 'service',
          },
        },
        hideAsterisk: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'emails',
        label: 'Emails',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDEmails',
          props: {
            title: 'Emails',
            recordType: 'service',
          },
        },
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'web',
        label: 'Website',
        type: 'text',
        rules: {
          required: false,
          pattern: {
            value: /(www\.|http:\/\/|https:\/\/)(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/,
            message: 'That does not look like a valid web address, please try again.',
          },
        },
        defaultValue: '',
        hideAsterisk: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'social_media',
        label: 'Socials',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDSocials',
          props: {
            title: 'Social Media',
            recordType: 'service',
          },
        },
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'opening_hours',
        label: 'Opening Hours',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDOpeningHours',
          props: {
            title: 'Opening Hours',
            recordType: 'service',
          },
        },
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'opening_hours_simple',
        label: 'Opening Hours',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        serviceProviderEditable: true,
        serviceProviderFormOnly: true,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'operates_after_hours',
        label: 'After Hours',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        serviceProviderEditable: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'availability',
        label: 'Availability',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
    ],
  },
  {
    title: 'Operational',
    ref: React.createRef(),
    icon: <FontAwesomeIcon icon={faUsersRectangle as IconProp}/>,
    fields: [
      {
        name: 'is_statewide',
        label: 'Statewide',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        serviceProviderEditable: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'catchment_description',
        label: 'Catchment Description',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        canShowLineBreaks: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'catchment',
        label: 'Geographic Coverage',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDGeographicCoverage',
          props: {
            title: 'Geographic Coverage',
            recordType: 'service',
          },
        },
        serviceProviderEditable: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'referral_info',
        label: 'Referral Info',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'special_requirements',
        label: 'Special Requirements',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'assessment_criteria',
        label: 'Assessment Criteria',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'intake_info',
        label: 'Intake Information',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'intake_point',
        label: 'Intake Point',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'cost',
        label: 'Cost',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'is_free_or_low_cost',
        label: 'Free or Low Cost',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'is_bulk_billing',
        label: 'Provides Bulk Billing',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'accepts_healthcare_cards',
        label: 'Accepts Health Care Cards',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'billing_method',
        label: 'Billing Method',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        bulkUpdateEnabled: true,
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'location_type',
        label: 'Location Type',
        type: 'dropdown',
        rules: {},
        defaultValue: '',
        options: [
          { name: 'Permanent', id: 'Permanent' },
          { name: 'Mobile', id: 'Mobile' },
          { name: 'Temporary', id: 'Temporary' },
          { name: 'Statewide', id: 'Statewide' },
          { name: 'National', id: 'National' },
        ],
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'location_expiry',
        label: 'Temporary Until',
        type: 'date',
        dateFormat: 'dd/MM/yyyy',
        rules: {},
        defaultValue: '',
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'accreditation',
        label: 'Accreditation',
        type: 'customComponent',
        rules: {},
        defaultValue: [],
        customComponent: {
          name: 'SPUDAccreditation',
          props: {
            title: 'Accreditation',
            formKey: 'accreditation',
          },
        },
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'practitioner_gender',
        label: 'Practitioner Gender',
        type: 'radio',
        rules: {},
        defaultValue: 'unspecified',
        options: [
          { id: 'male', name: 'Male' },
          { id: 'female', name: 'Female' },
          { id: 'unspecified', name: 'Unspecified' },
        ],
        serviceProviderEditable: true,
        allowAcceptServiceProviderChange: true,
      },
      {
        name: 'show_in_askizzy_health',
        label: 'Show in Ask Izzy Categories',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
      {
        name: 'sa_priority_service',
        label: 'SA Priority Service',
        type: 'toggle',
        rules: {},
        defaultValue: false,
        allowAcceptServiceProviderChange: false,
      },
    ],
  },
  {
    title: 'Legacy',
    ref: React.createRef(),
    icon: <FontAwesomeIcon icon={faClockRotateLeft as IconProp} />,
    hide: true,
    fields: [
      {
        name: 'details',
        label: 'Details',
        type: 'textarea',
        rules: {},
        defaultValue: '',
        canShowLineBreaks: true,
        allowAcceptServiceProviderChange: false,
      },
    ],
  },
]

export const SERVICE_FORM_DATA: Payload = {
  record_type: 'service',
  iss_id: null,
  status: 'draft',
  data: {},
}
